import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClockPreview } from '../components/ClockPreview';
import { OptionTiles } from '../components/configurateur/OptionTiles';
import { ThemePicker } from '../components/configurateur/ThemePicker';
import { Recapitulatif } from '../components/configurateur/Recapitulatif';
import { diametres, frames, hands, mecanismes, numerals } from '../data/options';
import { themes } from '../data/themes';
import type { Configuration } from '../types/clock';
const CONFIG_INITIALE: Configuration = {
  themeId: 'chevaux',
  photoPerso: null,
  texte: 'Passion Chevaux',
  sousTexte: 'Famille Dubois · 2026',
  numerals: 'arabes',
  hands: 'noir',
  frame: 'aucun',
  diametre: 30,
  mecanisme: 'silencieux'
};
function Etape({
  numero,
  titre,
  children
}: {
  numero: number;
  titre: string;
  children: React.ReactNode;
}) {
  return <section aria-labelledby={`etape-${numero}`} className="border-t border-line pt-8" data-id="element-124" data-mp="1051vccda0yyl">
      <div className="flex items-center gap-3" data-id="element-125" data-mp="1zusybkgi9nye">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-forest text-xs font-medium text-cream" data-id="element-126" data-mp="1zrx0rx8kl1ah">
          {numero}
        </span>
        <h2 id={`etape-${numero}`} className="font-display text-xl text-ink" data-id="element-127" data-mp="28tn2px02vimi">
          {titre}
        </h2>
      </div>
      <div className="mt-5 space-y-6" data-id="element-128" data-mp="2dmzdp4bu3qws">{children}</div>
    </section>;
}
export function Configurateur() {
  const [params] = useSearchParams();
  const themeParam = params.get('theme');
  const [config, setConfig] = useState<Configuration>(() => ({
    ...CONFIG_INITIALE,
    themeId: themes.some(t => t.id === themeParam) ? themeParam as string : CONFIG_INITIALE.themeId
  }));
  const [valide, setValide] = useState(false);
  useEffect(() => {
    if (themeParam && themes.some(t => t.id === themeParam)) {
      setConfig(c => ({
        ...c,
        themeId: themeParam,
        photoPerso: null
      }));
    }
  }, [themeParam]);
  const maj = <K extends keyof Configuration,>(cle: K, valeur: Configuration[K]) => {
    setValide(false);
    setConfig(c => ({
      ...c,
      [cle]: valeur
    }));
  };
  const visuel = useMemo(() => {
    if (config.photoPerso) return config.photoPerso;
    return themes.find(t => t.id === config.themeId)?.image ?? themes[0].image;
  }, [config.photoPerso, config.themeId]);
  return <main className="mx-auto max-w-6xl px-5 py-10 lg:py-16" data-id="element-129" data-mp="2afgp8r4efnzu">
      <header className="max-w-2xl" data-id="element-130" data-mp="2ofhnrzx6yjc5">
        <p className="text-xs uppercase tracking-[0.22em] text-terracotta" data-id="element-131" data-mp="2oeq0rdhnttxh">Configurateur</p>
        <h1 className="mt-3 font-display text-3xl leading-tight text-ink sm:text-4xl" data-id="element-132" data-mp="2jg51bga8fea3">
          Composez votre horloge, elle se dessine en direct
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-133" data-mp="2t6zpiq71gny6">
          Chaque changement s'applique immédiatement sur l'aperçu. Vous recevez un bon à tirer avant
          impression.
        </p>
      </header>

      <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:gap-14" data-id="element-134" data-mp="327xsd55qqpf6">
        <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-135" data-mp="3lsprxypgonwt">
          <div className="rounded-3xl border border-line bg-cream p-6 sm:p-8" data-id="element-136" data-mp="3lr4xsa7276zh">
            <ClockPreview image={visuel} numerals={config.numerals} hands={config.hands} frame={config.frame} texte={config.texte} sousTexte={config.sousTexte} mecanisme={config.mecanisme} data-id="element-137" data-mp="3qrj49nt0i8ve" />
            <p className="mt-6 text-center text-xs text-muted" data-id="element-138" data-mp="331f09fbww73v">
              Aperçu à l'échelle · diamètre {config.diametre} cm
            </p>
          </div>
        </div>

        <div className="space-y-8" data-id="element-139" data-mp="2t5lf57k4n5ah">
          <Etape numero={1} titre="Votre visuel" data-id="element-140" data-mp="2t2h5hunw7hcf">
            <ThemePicker themeId={config.themeId} photoPerso={config.photoPerso} onSelectTheme={id => {
            setValide(false);
            setConfig(c => ({
              ...c,
              themeId: id,
              photoPerso: null
            }));
          }} onUpload={url => maj('photoPerso', url)} onRemovePhoto={() => maj('photoPerso', null)} data-id="element-141" data-mp="2y2vg9kgvp5e5" />
          </Etape>

          <Etape numero={2} titre="Votre texte" data-id="element-142" data-mp="2c3pprl2m0p0n">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-143" data-mp="27c7n8skxugdk">
              <label className="block" data-id="element-144" data-mp="27amt342jczg8">
                <span className="text-sm font-medium text-ink" data-id="element-145" data-mp="23b47s76symcv">Texte principal</span>
                <input type="text" maxLength={22} value={config.texte} onChange={e => maj('texte', e.target.value)} placeholder="Passion Chevaux" className="mt-2 w-full rounded-xl border border-line bg-parchment px-4 py-3 text-sm text-ink outline-none transition-colors duration-150 ease-out placeholder:text-muted/60 focus:border-forest" data-id="element-146" data-mp="2391hrn5ngt85" />
                <span className="mt-1.5 block text-xs text-muted" data-id="element-147" data-mp="3ir332hvbmnvp">
                  {config.texte.length}/22 caractères
                </span>
              </label>
              <label className="block" data-id="element-148" data-mp="10j1s8gn7ukrh">
                <span className="text-sm font-medium text-ink" data-id="element-149" data-mp="10i04pcp0k5sv">Seconde ligne (facultatif)</span>
                <input type="text" maxLength={28} value={config.sousTexte} onChange={e => maj('sousTexte', e.target.value)} placeholder="Prénom, date, dédicace…" className="mt-2 w-full rounded-xl border border-line bg-parchment px-4 py-3 text-sm text-ink outline-none transition-colors duration-150 ease-out placeholder:text-muted/60 focus:border-forest" data-id="element-150" data-mp="15fif77ioit2z" />
                <span className="mt-1.5 block text-xs text-muted" data-id="element-151" data-mp="qjawycqu01qc">
                  {config.sousTexte.length}/28 caractères
                </span>
              </label>
            </div>
          </Etape>

          <Etape numero={3} titre="Cadran et aiguilles" data-id="element-152" data-mp="21yprl9vkihia">
            <OptionTiles legend="Repères des heures" options={numerals} value={config.numerals} onChange={v => maj('numerals', v)} data-id="element-153" data-mp="26y2gs19ww0o1" />
            <OptionTiles legend="Aiguilles" options={hands} value={config.hands} onChange={v => maj('hands', v)} data-id="element-154" data-mp="2v0s34vc511f" />
            <OptionTiles legend="Mécanisme" options={mecanismes} value={config.mecanisme} onChange={v => maj('mecanisme', v)} columns={2} data-id="element-155" data-mp="bv5xxakvrrdh" />
          </Etape>

          <Etape numero={4} titre="Format et finition" data-id="element-156" data-mp="3fd7dk5wla6e7">
            <OptionTiles legend="Diamètre" options={diametres} value={config.diametre} onChange={v => maj('diametre', v)} showBasePrice data-id="element-157" data-mp="3u0ioq6ouzods" />
            <OptionTiles legend="Cadre" options={frames} value={config.frame} onChange={v => maj('frame', v)} data-id="element-158" data-mp="31jpvyqngr3qt" />
          </Etape>

          <Recapitulatif config={config} valide={valide} onValider={() => setValide(true)} data-id="element-159" data-mp="1c3410hs44t49" />
        </div>
      </div>
    </main>;
}